// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { mergeWithCss } from "@sc/XDL/Utilities";
import React, { FC, PropsWithChildren, useMemo, useState } from "react";

interface ProductGridContainerContextType {
    styles: ProductGridContainerStyle;
}
export interface ProductGridContainerStyle {
    container?: InjectableCss;
    info?: InjectableCss;
    addToList?: InjectableCss;
    price?: InjectableCss;
    availability?: InjectableCss;
    priceAndActions?: InjectableCss;
    addToCartActions?: InjectableCss;
    image?: InjectableCss;
    badges?: InjectableCss;
}

const enum NAMES {
    Image = "Image",
    Info = "Info",
    Price = "Price",
    PriceAndActions = "PriceAndActions",
    AddToCartActions = "AddToCartActions",
    Availability = "Availability",
    AddToList = "AddToList",
    Badges = "Badges",
}

export const productGridContainerStyle: ProductGridContainerStyle = {};

const ProductGridContainerContext = React.createContext<ProductGridContainerContextType>({
    styles: {},
});

const ProductGridContainer = ({
    children,
    extendedStyles,
    ...otherProps
}: {
    children: any;
    extendedStyles?: ProductGridContainerStyle;
    [key: string]: any;
}) => {
    const [styles] = useState(() => mergeWithCss(productGridContainerStyle, extendedStyles));
    const value = useMemo(() => ({ styles }), [styles]);
    return (
        <ProductGridContainerContext.Provider value={value}>
            <StyledWrapper {...styles.container} {...otherProps}>
                {children}
            </StyledWrapper>
        </ProductGridContainerContext.Provider>
    );
};

export const useProductGridContainerContext = () => {
    const context = React.useContext(ProductGridContainerContext);
    if (!context) {
        throw new Error("ProductGridTemplate compound components cannot be rendered outside ProductGridContainer");
    }
    return context;
};

const Image: FC<PropsWithChildren> = ({ children, ...otherProps }) => {
    const {
        styles: { image },
    } = useProductGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...image}>
            {children}
        </StyledWrapper>
    );
};

const Info: FC<PropsWithChildren> = ({ children, ...otherProps }) => {
    const {
        styles: { info },
    } = useProductGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...info}>
            {children}
        </StyledWrapper>
    );
};

const AddToCartActions: FC<PropsWithChildren> = ({ children, ...otherProps }) => {
    const {
        styles: { addToCartActions },
    } = useProductGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...addToCartActions}>
            {children}
        </StyledWrapper>
    );
};

const AddToList: FC<PropsWithChildren> = ({ children, ...otherProps }) => {
    const {
        styles: { addToList },
    } = useProductGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...addToList} data-test-selector="productCarousel-AddToListActions">
            {children}
        </StyledWrapper>
    );
};

const Badges: FC<PropsWithChildren> = ({ children, ...otherProps }) => {
    const {
        styles: { badges },
    } = useProductGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...badges} data-test-selector="productCarousel-BadgeActions">
            {children}
        </StyledWrapper>
    );
};
const PriceAndActions: FC<PropsWithChildren> = ({ children, ...otherProps }) => {
    const {
        styles: { priceAndActions },
    } = useProductGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...priceAndActions} className="product-grid-container-priceAndActions">
            {children}
        </StyledWrapper>
    );
};

const Price: FC<PropsWithChildren> = ({ children, ...otherProps }) => {
    const {
        styles: { price },
    } = useProductGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...price}>
            {children}
        </StyledWrapper>
    );
};

const Availability: FC<PropsWithChildren> = ({ children, ...otherProps }) => {
    const {
        styles: { availability },
    } = useProductGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...availability}>
            {children}
        </StyledWrapper>
    );
};

ProductGridContainer.Image = Image;
ProductGridContainer.Image.displayName = NAMES.Image;
ProductGridContainer.Info = Info;
ProductGridContainer.Info.displayName = NAMES.Info;
ProductGridContainer.PriceAndActions = PriceAndActions;
ProductGridContainer.PriceAndActions.displayName = NAMES.PriceAndActions;
ProductGridContainer.AddToCartActions = AddToCartActions;
ProductGridContainer.AddToCartActions.displayName = NAMES.AddToCartActions;
ProductGridContainer.Price = Price;
ProductGridContainer.Price.displayName = NAMES.Price;
ProductGridContainer.Availability = Availability;
ProductGridContainer.Availability.displayName = NAMES.Availability;
ProductGridContainer.AddToList = AddToList;
ProductGridContainer.AddToList.displayName = NAMES.AddToList;
ProductGridContainer.Badges = Badges;
ProductGridContainer.Badges.displayName = NAMES.Badges;

export default ProductGridContainer;
